import type {
  LoadedScript,
  ScriptCapabilities,
  ScriptCapabilityApi,
  ScriptHook,
  ScriptHost,
  ScriptLanguage,
  ScriptLimits,
} from "@roqeet/core";
import { DEFAULT_SCRIPT_LIMITS } from "@roqeet/core";
import { QuickJsScriptHost } from "./quickjs-host.js";
import { LuaScriptHost } from "./lua-host.js";

/**
 * 언어별 스크립트 호스트 라우터 — 결정 #4/#6/#10. core의 단일 ScriptHost
 * 인터페이스 뒤에서 JS(QuickJS)와 Lua(wasmoon)를 적절한 엔진으로 보낸다.
 *
 * 각 엔진의 WASM은 해당 언어 스크립트가 처음 load될 때만 lazy-load된다(결정 #4 §6).
 * 따라서 Lua를 전혀 쓰지 않는 사용자는 Lua WASM을 절대 내려받지 않는다.
 */
export class CompositeScriptHost implements ScriptHost {
  private readonly js: QuickJsScriptHost;
  private readonly lua: LuaScriptHost;

  constructor(limits: ScriptLimits = DEFAULT_SCRIPT_LIMITS) {
    this.js = new QuickJsScriptHost(limits);
    this.lua = new LuaScriptHost(limits);
  }

  supports(language: ScriptLanguage): boolean {
    return this.js.supports(language) || this.lua.supports(language);
  }

  load(
    def: {
      id: string;
      language: ScriptLanguage;
      source: string;
      hooks: ScriptHook[];
      capabilities: ScriptCapabilities;
      consentGranted: boolean;
    },
    api: ScriptCapabilityApi,
  ): Promise<LoadedScript> {
    if (def.language === "lua") return this.lua.load(def, api);
    if (def.language === "javascript") return this.js.load(def, api);
    return Promise.reject(
      new Error(`지원하지 않는 스크립트 언어입니다: ${def.language}`),
    );
  }

  dispose(): void {
    this.js.dispose();
    this.lua.dispose();
  }
}
